import { ButtonCompoundVariants } from '@library/common/utils/class-variants';
import { cva, VariantProps } from 'class-variance-authority';
import { ButtonHTMLAttributes, DetailedHTMLProps, FC, ForwardedRef, forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';
import styles from './Button.module.scss';

const iconButtonVariants = cva(
    [
        'inline-flex items-center justify-center',
        'whitespace-nowrap',
        'font-semibold',
        'cursor-pointer',
        'transition-all duration-200 hover:brightness-110',
    ],
    {
        variants: {
            color: {
                primary: [],
                secondary: [],
                success: [],
                danger: [],
                warning: [],
            },
            variant: {
                default: [],
                shaded: [styles['shaded-elevation']],
                outlined: [],
                text: [],
            },
            size: {
                xs: ['text-xs w-8 h-8 rounded-lg'],
                sm: ['text-sm w-9 h-9 rounded-[10px]'],
                normal: ['text-base w-10 h-10 rounded-[10px]'],
                lg: ['text-lg w-11 h-11 rounded-xl'],
                xl: ['text-xl w-12 h-12 rounded-xl'],
            },
        },
        compoundVariants: ButtonCompoundVariants,
        defaultVariants: {
            color: 'primary',
            variant: 'default',
            size: 'normal',
        },
    }
);

export interface IconButtonProps
    extends Omit<DetailedHTMLProps<ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>, 'color'>,
        VariantProps<typeof iconButtonVariants> {
    ref?: ForwardedRef<HTMLButtonElement>;
}

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
    (props: IconButtonProps, ref: ForwardedRef<HTMLButtonElement>) => {
        const { children, variant, color, size, className, ...rest } = props;

        return (
            <button ref={ref} {...rest} className={twMerge(iconButtonVariants({ color, variant, size, className }))}>
                {children}
            </button>
        );
    }
);

export default IconButton;
